import { useQuery } from '@tanstack/react-query'
import {
  Box, Typography, Chip, CircularProgress, Paper,
  Table, TableBody, TableCell, TableHead, TableRow, Tooltip,
} from '@mui/material'
import { getApiInvocations } from '../../api/client'
import type { ApiInvocation } from '../../api/types'

function statusColor(status: number): 'success' | 'warning' | 'error' | 'default' {
  if (status >= 200 && status < 300) return 'success'
  if (status === 401 || status === 403) return 'warning'
  if (status >= 400) return 'error'
  return 'default'
}

function duration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

const METHOD_COLOR: Record<string, string> = {
  GET: '#3b82f6',
  POST: '#10b981',
  PUT: '#f59e0b',
  DELETE: '#ef4444',
}

export default function ApiInvocationsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['api-invocations'],
    queryFn: () => getApiInvocations(100),
    refetchInterval: 10_000,
  })

  const invocations = data?.data ?? []

  return (
    <Box>
      <Box mb={2}>
        <Typography variant="h5" fontWeight={600}>API Invocations</Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Every call to a [RequireApiKey]-gated endpoint — pipeline triggers, runbook triggers, the
          EF migration check, and anything else meant to be called by a pipeline rather than a
          signed-in browser. Refreshes every 10s.
        </Typography>
      </Box>

      {isLoading ? (
        <CircularProgress />
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: 'grey.50' } }}>
                <TableCell>When</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Path</TableCell>
                <TableCell>Arguments</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Duration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invocations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                    No invocations recorded yet — they show up here the next time a pipeline calls
                    a [RequireApiKey]-gated endpoint.
                  </TableCell>
                </TableRow>
              ) : invocations.map((inv: ApiInvocation) => {
                const detail = inv.arguments ?? inv.queryString ?? ''
                return (
                  <TableRow key={inv.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: 12 }}>
                      {new Date(inv.invokedAt).toLocaleString()}
                    </TableCell>

                    <TableCell>
                      <Typography fontFamily="monospace" fontWeight={700} fontSize={12}
                        sx={{ color: METHOD_COLOR[inv.method] ?? 'text.primary' }}>
                        {inv.method}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {inv.path}
                    </TableCell>

                    <TableCell sx={{ maxWidth: 360 }}>
                      {detail ? (
                        <Tooltip title={detail} placement="top-start">
                          <Typography fontFamily="monospace" fontSize={11} color="text.secondary"
                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {detail}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography fontSize={12} color="text.disabled">—</Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Chip label={inv.statusCode} color={statusColor(inv.statusCode)} size="small" variant="outlined"
                        sx={{ fontFamily: 'monospace', fontWeight: 700, minWidth: 52 }} />
                    </TableCell>

                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}>
                      {duration(inv.durationMs)}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  )
}
